// import '@ant-design/compatible/assets/index.css';
import { Alert, Button, Divider, Spin, message } from 'antd';
import isEmpty from 'lodash/isEmpty';
import React from 'react';

import { Form } from '@ant-design/compatible';
import { getDimensionList } from '@src/api/common/query';
import ModalContext from '@src/context';
import {
  Dimension,
  DimensionViewModel,
  FieldViewModel,
  commonOperators,
} from '@src/models/filters';
import { getFilterFromRules, getRulesFromFilter } from '@utils';
import featureNames from '@utils/featureToggle/constants';

import containerStyles from '../FilterContainer.less';
import FilterRuleGroup from './FilterRuleGroup';
import styles from './NewFilterPanel.less';

type NewFilterPanelState = {
  loading: boolean;
};

export default class NewFilterPanel extends React.PureComponent<any, NewFilterPanelState> {
  static contextType = ModalContext;

  constructor(props) {
    super(props);
    this.state = {
      loading: true,
    };
  }

  async componentDidMount() {
    const { updateMeta, dimensions, setInitialRule } = this.props;
    const loading = isEmpty(dimensions);
    this.setState({
      loading,
    });
    try {
      const resp = await getDimensionList();
      const data = this.transform(resp?.data?.data);
      updateMeta({}, data);

      if (loading) {
        setInitialRule({
          field: data[0].fields[0].value,
          op: 'eq',
          value: '',
          key: '',
          display: '',
        });
        this.setState({
          loading: false,
        });
      }
    } catch (err) {
      message.error(err.message);
    }
  }

  render() {
    let elt;
    const { loading } = this.state;
    if (loading) {
      elt = <Spin />;
    } else {
      const { dimensions, rule } = this.props;
      elt = (
        <>
          <Form layout="vertical" className={styles.newFilterPanel}>
            <Form.Item label="Rules" required>
              <FilterRuleGroup dimensions={dimensions} rule={rule} />
            </Form.Item>
          </Form>
          <Divider style={{ margin: '0 0 1rem 0' }} />
          {this.renderButtons()}
        </>
      );
    }
    return elt;
  }

  onApply = () => {
    const { type, fid, applyFilter, updateFilter } = this.props;
    const filterData = this.createFilterData();
    const rules = getRulesFromFilter(filterData.rule, true);
    const tmpFilter = getFilterFromRules(rules, true);
    if (!filterData.name) {
      filterData.name = tmpFilter.name;
    }

    if (type === 'create') {
      applyFilter(filterData);
    } else {
      updateFilter(fid, filterData);
    }
  };

  createFilterData() {
    const { rule, name, isContent } = this.props;
    const filterData = {
      name,
      rule,
      is_content: isContent,
    };
    return filterData;
  }

  isInvalidXMLString = (string) => {
    let match = false;

    // Regex for all not supported characters, currently we are skipping the discouraged charaters.
    const regex = new RegExp(
      // eslint-disable-next-line no-control-regex
      /((?:[\0-\x08\x0B\f\x0E-\x1F\uFFFD\uFFFE\uFFFF]|[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?:[^\uD800-\uDBFF]|^)[\uDC00-\uDFFF]))/g,
    );
    match = regex.test(string);

    return match;
  };

  validateXMLRule = (name, rule) => {
    if (this.isInvalidXMLString(name)) {
      return {
        isXmlRuleInvalid: true,
        xmlErrorMsg: `Filter name '${name}' has invalid characters which are not supported.`,
      };
    }
    for (let i = 0; i < rule.length; i += 1) {
      const andRule = rule[i];
      for (let j = 0; j < andRule.length; j += 1) {
        const orRule = andRule[j];
        const { field, value } = orRule;
        if (field === 'OBJECT_NAME' && this.isInvalidXMLString(value)) {
          return {
            isXmlRuleInvalid: true,
            xmlErrorMsg: `Filter rule value '${value}' has invalid characters which are not supported.`,
          };
        }
      }
    }
    return { isXmlRuleInvalid: false, xmlErrorMsg: '' };
  };

  validation = (name, rule) => {
    const { features } = this.props;
    if (!name) {
      return false;
    }
    for (let i = 0; i < rule.length; i += 1) {
      const andRule = rule[i];
      for (let j = 0; j < andRule.length; j += 1) {
        const orRule = andRule[j];
        const { field, key, value } = orRule;
        if (value === '') {
          return false;
        }
        if (field === 'TAG' && (key === '' || value === '')) {
          return false;
        }
      }
    }
    return features[featureNames.CREATE_FILTER];
  };

  renderButtons = () => {
    const { onCancel, rule, name, type } = this.props;

    const { isXmlRuleInvalid, xmlErrorMsg } = this.validateXMLRule(name, rule);

    return (
      <div className={containerStyles.buttons}>
        {isXmlRuleInvalid && (
          <Alert message={xmlErrorMsg} type="error" className={styles.alertPanel} />
        )}
        <Button className={containerStyles.leftButton} onClick={onCancel} type="primary" ghost>
          Cancel
        </Button>
        <div className={containerStyles.rightBtn}>
          <Button
            type="primary"
            ghost
            disabled={!this.validation('name', rule)}
            onClick={this.onApply}
          >
            {type === 'create' ? 'Apply' : 'Update'}
          </Button>
        </div>
      </div>
    );
  };

  transform = (data: Dimension[]): DimensionViewModel[] => {
    const map = new Map();
    (data ?? [])
      .filter(
        (dimension) =>
          dimension.dimension_name && dimension.dimension_type && dimension.display_name,
      )
      .forEach((dimension) => {
        const category = dimension.dimension_type;
        const a = map.get(category);
        const newDimension: FieldViewModel = {
          name: dimension.display_name,
          value: dimension.dimension_name,
          operators: commonOperators,
          values: [],
          urls: {},
          autocompletion_only: false,
        };
        if (a) {
          map.set(category, [...a, newDimension]);
        } else {
          map.set(category, [newDimension]);
        }
      });
    const res: DimensionViewModel[] = [];
    for (const [key, value] of map.entries()) {
      res.push({
        fields: value,
        name: key,
      });
    }
    return res;
  };
}
